// DataTableV2 — the shared table shell (ticket §5.0: "a platform investment,
// not just a feature").
//
// Owns the GENERIC table concerns our older tables couldn't do in one place:
//   • column drag-to-reorder (the capability this component exists for)
//   • sorting (desc → asc → off cycle on the headers)
//   • hide / show columns (the Columns menu; the leading column is pinned)
//   • search (TableSearchInput, mounted wherever the host page wants it)
//   • filtering (a toolbar slot — presets differ per table, see FiltersMenu)
//   • pagination, loading / refetch / empty states, sticky-edge shadows
//
// It deliberately does NOT own the rows: `children` renders the <tbody>
// content, so a host can layer feature-specific row behaviour — the Analytics
// grid composes lazy drill-down and an independent expandable detail region on
// top — without this component knowing about it. Column/sort/visibility state
// is CONTROLLED (props + change callbacks) because hosts persist it (e.g. into
// a saved view) and read it back (e.g. the PDF snapshot).
//
// First consumer: the Analytics report's Dimensional analysis grid. The
// scheduled-reports page re-implements several of these parts today and is the
// natural second adopter (migration explicitly out of scope for DEV-2377).
import { type ReactNode } from 'react'
import { AnimatePresence } from 'framer-motion'
import {
	DotMatrix,
	Pagination,
	Table,
	TableBody,
	TableHeader,
	TableRow
} from '@clickguard/design-system'
import { SortableHead, TableEmptyState } from './table-parts'
import { ColumnsMenu } from './menus'
import { useColumnDrag } from './use-column-drag'

/** A data column: key + header label + how a row renders its cell. `render` is
 *  consumed by the host's row renderer (rows are host-drawn); the shell needs
 *  only key/label for the header and the Columns menu. */
export interface DataTableColumn<Row, K extends string = string> {
	key: K
	label: string
	/** Extra classes for the column's body cells (host-side convention). */
	cellClassName?: string
	render: (row: Row) => ReactNode
	sortValue?: (row: Row) => number
}

export interface DataTableSort<K extends string = string> {
	/** A column key, or the leading column's sort key. */
	key: K | string
	dir: 'asc' | 'desc'
}

export interface DataTableV2Props<Row, K extends string = string> {
	/** Every column in natural order — the Columns menu lists these. */
	columns: Array<DataTableColumn<Row, K>>
	/** Display order over ALL column keys (hidden ones keep their slot). */
	columnOrder: K[]
	onColumnOrderChange: (next: K[]) => void
	visibleColumns: Set<K>
	onToggleColumn: (k: K) => void
	/** The pinned leading column (always visible, always first). */
	leading: { label: string; className?: string; sortKey: string }
	sort: DataTableSort<K> | null
	/** Receives the next sort on header click (desc → asc → null cycle). */
	onSortChange: (next: DataTableSort<K> | null) => void
	/** Flips headers into drag handles (Customize mode). */
	editMode?: boolean
	/** Left side of the toolbar row (pickers, filters, chips…). */
	toolbarLeading?: ReactNode
	/** Right side of the toolbar row, before the built-in Columns menu. */
	toolbarTrailing?: ReactNode
	/** From the host's useScrollEdges — the shell renders the scroll viewport,
	 *  the host also needs `edge` for its sticky body cells. */
	scrollRef: React.MutableRefObject<HTMLDivElement | null>
	onScroll: () => void
	edgeStart: boolean
	tableClassName?: string
	/** Remount key for the body's AnimatePresence scope (e.g. `page:pageSize`).
	 *  One presence scope across pages lets an interrupted row exit pin stale
	 *  rows forever — see the Analytics grid's audit note. */
	bodyKey: string
	/** The <tbody> rows (host-rendered — drill-down etc. live with the host). */
	children: ReactNode
	hasRows: boolean
	isLoading: boolean
	/** True while refetching with rows still on screen → overlay loader. */
	isFetching: boolean
	loadingLabel?: string
	/** Shown when there are no rows and nothing is loading. */
	emptyState?: ReactNode
	pagination: {
		page: number
		totalPages: number
		onPageChange: (p: number) => void
		pageSize: number
		pageSizeOptions: number[]
		onPageSizeChange: (s: number) => void
	}
}

export const DataTableV2 = <Row, K extends string = string>({
	columns,
	columnOrder,
	onColumnOrderChange,
	visibleColumns,
	onToggleColumn,
	leading,
	sort,
	onSortChange,
	editMode = false,
	toolbarLeading,
	toolbarTrailing,
	scrollRef,
	onScroll,
	edgeStart,
	tableClassName,
	bodyKey,
	children,
	hasRows,
	isLoading,
	isFetching,
	loadingLabel = 'Loading table',
	emptyState,
	pagination
}: DataTableV2Props<Row, K>) => {
	// Visible columns in display order — the header row.
	const cols = columnOrder
		.map((k) => columns.find((c) => c.key === k))
		.filter((c): c is DataTableColumn<Row, K> => !!c && visibleColumns.has(c.key))
	const { dragCol, dropCol, startColDrag, overCol, endColDrag, dropOnCol } = useColumnDrag({
		columnOrder,
		visibleColumns,
		onColumnOrderChange
	})
	// Sort cycle: first click sorts desc, second asc, third clears.
	const cycleSort = (key: K | string) =>
		onSortChange(
			!sort || sort.key !== key
				? { key, dir: 'desc' }
				: sort.dir === 'desc'
				? { key, dir: 'asc' }
				: null
		)

	return (
		<>
			{/* Toolbar — host slots left/right + the built-in Columns menu. */}
			<div className="flex flex-wrap items-center gap-2">
				{toolbarLeading}
				<div className="ml-auto flex items-center gap-2">
					{toolbarTrailing}
					<ColumnsMenu columns={columns} visible={visibleColumns} onToggle={onToggleColumn} pinnedLabel={leading.label} />
				</div>
			</div>

			{/* Table + pagination form ONE block with no gap between them.
			    `relative` anchors the refetch loader overlay below. */}
			<div className="relative">
				{/* `isolate` traps the sticky columns' z-index inside this card so the
				    frozen leading cells can't punch through page-level fixed overlays.
				    The scrollbar utilities hide the app's always-on 6px purple global
				    webkit scrollbar (App.css) on the table's horizontal overflow — the
				    edge shadow already signals there's more to scroll. Wheel/trackpad
				    scrolling still works. */}
				<div
					ref={scrollRef}
					className="isolate overflow-x-auto rounded-lg [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
					onScroll={onScroll}
				>
					<Table className={tableClassName}>
						<TableHeader className="bg-[rgb(var(--cg-canvas))]">
							<TableRow className="hover:!bg-transparent">
								<SortableHead
									label={leading.label}
									className={leading.className}
									sticky
									scrolled={!edgeStart}
									active={sort?.key === leading.sortKey}
									dir={sort?.key === leading.sortKey ? sort.dir : 'desc'}
									onClick={() => cycleSort(leading.sortKey)}
								/>
								{cols.map((c, ci) => (
									<SortableHead
										key={c.key}
										label={c.label}
										active={sort?.key === c.key}
										dir={sort?.key === c.key ? sort.dir : 'desc'}
										onClick={() => cycleSort(c.key)}
										editMode={editMode}
										draggable
										onDragStart={(e) => startColDrag(ci, e)}
										onDragEnd={endColDrag}
										onDragOver={(e) => overCol(ci, e)}
										onDrop={dropOnCol}
										dropSide={editMode && dropCol && dropCol.index === ci && dropCol.index !== dragCol ? dropCol.side : null}
										dragging={editMode && dragCol === ci}
									/>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{/* Keyed on the host's page scope so a page change REMOUNTS the
							    presence scope (see bodyKey doc above). */}
							<AnimatePresence key={bodyKey} initial={false}>
								{children}
							</AnimatePresence>
						</TableBody>
					</Table>
					{!hasRows &&
						(isLoading ? (
							<div className="flex items-center justify-center py-16">
								<DotMatrix size="md" label={loadingLabel} />
							</div>
						) : (
							emptyState ?? null
						))}
				</div>
				{/* Refetch overlay: while the table reloads the host keeps the previous
				    rows visible — surface a loader over them so a slow query doesn't
				    look frozen. */}
				{hasRows && isFetching && (
					<div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center pt-16">
						<DotMatrix size="md" label={loadingLabel} />
					</div>
				)}

				<Pagination
					page={pagination.page}
					totalPages={pagination.totalPages}
					onPageChange={pagination.onPageChange}
					pageSize={pagination.pageSize}
					pageSizeOptions={pagination.pageSizeOptions}
					onPageSizeChange={pagination.onPageSizeChange}
					showSummary
					showFirstLast
				/>
			</div>
		</>
	)
}

export { TableEmptyState }
